import { useCallback, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { GateStatus, JobReadiness, ReadinessGate } from '@/api/services/jobs.api';

/**
 * Whether a job can start, and what is stopping it.
 *
 * The verdict is the worst gate rather than a blend, and the panel is built to
 * make that legible: one headline, then every gate with its own finding whether
 * it passed or not. A reader shown only the failures cannot tell a job with
 * three gates green from one where nobody has looked at the other three.
 *
 * An unassessed gate is drawn as a question, not as absence. It is the state
 * that quietly sinks a mobilisation — nobody recorded the permits, so nobody
 * chased them — and greying it out is how it stays that way.
 *
 * Three decisions about how it is drawn, each rejecting a more obvious option:
 *
 * The strip across the top exists because five gates and three states is a
 * shape, and a shape is read faster than five paragraphs. Before it, telling
 * two-blocked-one-unknown-two-clear from any other combination meant reading
 * every row.
 *
 * Detail sits behind a disclosure rather than on the page. Two of these five
 * gates carry six lines of specifics between them, which was sixty per cent of
 * the panel's height and pushed the verdict — the thing it exists to state —
 * into the smallest type on it. What is blocking opens by default, because that
 * is what somebody is here to act on; what passed stays shut.
 *
 * Collapsible per gate rather than an Accordion over all five: only the gates
 * with findings have anything to open, and an accordion would put a chevron on
 * the three that do not.
 */

const VERDICT: Record<GateStatus, { label: string; blurb: string; className: string }> = {
  PASSED: {
    label: 'Ready to start',
    blurb: 'Every gate checked and clear.',
    className: 'bg-status-success-subtle text-status-success-foreground',
  },
  BLOCKED: {
    label: 'Not ready',
    blurb: 'At least one gate would stop this job today.',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  NOT_ASSESSED: {
    label: 'Unknown',
    blurb: 'Too little is recorded to say whether this job can start.',
    className: 'bg-status-warning-subtle text-status-warning-foreground',
  },
};

const GATE_ICON: Record<GateStatus, typeof CheckCircle2> = {
  PASSED: CheckCircle2,
  BLOCKED: XCircle,
  NOT_ASSESSED: HelpCircle,
};

const TONE: Record<GateStatus, { text: string; fill: string; border: string }> = {
  PASSED: {
    text: 'text-status-success-foreground',
    fill: 'bg-status-success',
    border: 'border-status-success-border',
  },
  BLOCKED: {
    text: 'text-status-destructive-foreground',
    fill: 'bg-status-destructive',
    border: 'border-status-destructive-border',
  },
  NOT_ASSESSED: {
    text: 'text-status-warning-foreground',
    fill: 'bg-status-warning',
    border: 'border-status-warning-border',
  },
};

/** The gate key is already the short name; the label spells out the test. */
const SHORT: Record<string, string> = {
  material: 'Material',
  equipment: 'Equipment',
  stock: 'Stock',
  transport: 'Transport',
  permits: 'Permits',
};

/**
 * Five gates and three states, read as a shape rather than as prose — and a way
 * into each one.
 *
 * The segments drive the rows below rather than opening panels of their own. A
 * panel per gate was the other option and would have put a second copy of all
 * five on the page, shown one at a time; the verdict here is the worst of five
 * and a reader who can see only one cannot judge it.
 */
function GateStrip({
  gates,
  onSelect,
  openKeys,
}: {
  gates: ReadinessGate[];
  onSelect: (key: string) => void;
  openKeys: Set<string>;
}) {
  return (
    <div className="flex gap-1.5">
      {gates.map((gate) => (
        <button
          key={gate.key}
          type="button"
          onClick={() => onSelect(gate.key)}
          aria-expanded={openKeys.has(gate.key)}
          aria-controls={`gate-${gate.key}`}
          className="group min-w-0 flex-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span
            className={cn(
              'block h-1.5 rounded-full transition-opacity group-hover:opacity-70',
              TONE[gate.status].fill
            )}
          />
          <span className="mt-1.5 block truncate text-[11px] text-muted-foreground transition-colors group-hover:text-foreground">
            {SHORT[gate.key] ?? gate.key}
          </span>
        </button>
      ))}
    </div>
  );
}

export function JobReadinessPanel({ readiness }: { readiness: JobReadiness }) {
  const verdict = VERDICT[readiness.verdict];
  const blocking = readiness.gates.find((g) => g.key === readiness.blockedBy);
  const checked = readiness.gates.filter((g) => g.status !== 'NOT_ASSESSED').length;

  // Held here rather than inside each row, because the strip above has to be
  // able to open one. Blocked gates start open: that is what somebody came for.
  const [openKeys, setOpenKeys] = useState<Set<string>>(
    () => new Set(readiness.gates.filter((g) => g.status === 'BLOCKED').map((g) => g.key))
  );
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggle = useCallback((key: string, next?: boolean) => {
    setOpenKeys((prev) => {
      const copy = new Set(prev);
      const shouldOpen = next ?? !copy.has(key);
      if (shouldOpen) copy.add(key);
      else copy.delete(key);
      return copy;
    });
  }, []);

  const select = useCallback(
    (key: string) => {
      toggle(key, true);
      // `nearest` rather than `start`: the page scrolls in an inner container
      // and forcing the row to the top would throw the verdict off screen,
      // which is the one thing the panel exists to state.
      rowRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
    [toggle]
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-base">Can this job start?</CardTitle>
              <CardDescription className="mt-1">
                {blocking ? blocking.statement : verdict.blurb}
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge className={verdict.className}>{verdict.label}</Badge>
              {readiness.confidence !== 'full' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-[11px] text-muted-foreground underline decoration-dotted underline-offset-2">
                      {readiness.confidence === 'none'
                        ? 'no gate has enough recorded to check'
                        : `${checked} of ${readiness.gates.length} gates checked`}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    A gate can be checked only once what it needs has been recorded. The rest are
                    unknown rather than clear, so this verdict carries less weight than one drawn
                    from all five.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="mt-4">
            <GateStrip gates={readiness.gates} onSelect={select} openKeys={openKeys} />
          </div>
        </CardHeader>

        <CardContent className="space-y-1 border-t pt-3">
          {readiness.gates.map((gate) => (
            <GateRow
              key={gate.key}
              gate={gate}
              decided={gate.key === readiness.blockedBy}
              open={openKeys.has(gate.key)}
              onOpenChange={(next) => toggle(gate.key, next)}
              rowRef={(el) => {
                rowRefs.current[gate.key] = el;
              }}
            />
          ))}

          <p className="border-t pt-3 text-xs text-muted-foreground">
            The verdict is the worst gate, not an average. A staged kit does not offset a lapsed
            certificate, and a gate with nothing recorded counts as unknown rather than clear.
          </p>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

function GateRow({
  gate,
  decided,
  open,
  onOpenChange,
  rowRef,
}: {
  gate: ReadinessGate;
  decided: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  const Icon = GATE_ICON[gate.status];
  const detail = gate.detail ?? [];

  const body = (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{gate.label}</span>
        {decided && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                sets the verdict
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              The worst gate decides the verdict, so this is the one setting it. Clearing it moves
              the verdict to whichever gate is next worst.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <p
        className={cn(
          'mt-0.5 text-xs',
          gate.status === 'NOT_ASSESSED'
            ? 'text-status-warning-foreground'
            : 'text-muted-foreground'
        )}
      >
        {gate.unassessedBecause ?? gate.statement}
      </p>
      {gate.action && (
        // One link, to where the work is actually done. Not on every gate: one
        // with nowhere useful to send anybody gets nothing, which is cheaper
        // than a click that ends on an empty list.
        <Link
          to={gate.action.href}
          onClick={(e) => e.stopPropagation()}
          className="mt-1.5 inline-flex items-center gap-1 rounded-sm text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {gate.action.label}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );

  // A gate with no findings has nothing to disclose, so it is a plain row —
  // but it still takes a ref, because the strip above can send the reader here
  // and landing on nothing at all would read as a broken control.
  if (detail.length === 0) {
    return (
      <div id={`gate-${gate.key}`} ref={rowRef} className="flex gap-2.5 rounded-lg px-2 py-2">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONE[gate.status].text)} />
        {body}
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} id={`gate-${gate.key}`} ref={rowRef}>
      <CollapsibleTrigger className="flex w-full gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONE[gate.status].text)} />
        {body}
        <span className="mt-0.5 flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
          {detail.length}
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className={cn('ml-8 mt-1 space-y-0.5 border-l-2 py-1 pl-3', TONE[gate.status].border)}>
          {detail.map((d) => (
            <li key={d} className="font-mono text-xs text-foreground">
              {d}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
